import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import type { User, Venue } from "@/types";

type Step = { id: string; done: boolean; to?: string; onClick?: () => void; optional?: boolean };

export const WelcomeGuide = ({ user, venues, onAddVenue }: { user: User; venues: Venue[]; onAddVenue: () => void }) => {
  const { t, lang } = usePrefs();
  const key = `wn_guide_dismissed_${user.user_id}`;
  const [hidden, setHidden] = useState(() => localStorage.getItem(key) === "1");
  const [events, setEvents] = useState<{ status: string }[] | null>(null);
  const [team, setTeam] = useState<unknown[] | null>(null);
  useEffect(() => {
    if (hidden) return;
    api.get("/venue/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    api.get("/venue/team").then((r) => setTeam(r.data)).catch(() => setTeam([]));
  }, [hidden]);
  if (hidden || !events || !team) return null;

  const first = venues[0];
  const steps: Step[] = [
    { id: "venue", done: venues.length > 0, onClick: onAddVenue },
    { id: "photos", done: venues.some((v) => (v.gallery || []).length > 0), to: first ? `/venue/venues/${first.venue_id}/edit` : undefined },
    { id: "event", done: events.some((e) => e.status === "published"), to: "/venue/events/new" },
    { id: "team", done: team.length > 1, to: "/venue/team", optional: true },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (steps.filter((s) => !s.optional).every((s) => s.done)) return null;
  const nextId = steps.find((s) => !s.done)?.id;
  const Arrow = lang === "ar" ? ChevronLeft : ChevronRight;
  const dismiss = () => {
    localStorage.setItem(key, "1");
    setHidden(true);
  };

  return (
    <section data-testid="welcome-guide" className="mb-6 rounded-3xl border bg-card p-5 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-1.5 bg-muted"><div className="h-full bg-primary transition-[width] duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} /></div>
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 shrink-0 rounded-2xl bg-primary/10 text-primary grid place-items-center"><Sparkles className="h-5 w-5" /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-black text-lg">{t("guide.title")}</p>
          <p className="text-sm text-muted-foreground">{t("guide.sub")}</p>
        </div>
        <span data-testid="welcome-guide-progress" className="text-xs font-bold text-muted-foreground mt-1"><bdi>{doneCount}/{steps.length}</bdi></span>
        <button onClick={dismiss} aria-label={t("guide.dismiss")} title={t("guide.dismiss")} data-testid="welcome-guide-dismiss" className="h-8 w-8 grid place-items-center rounded-full hover:bg-accent"><X className="h-4 w-4" /></button>
      </div>
      <ol className="mt-4 space-y-2">
        {steps.map((s, i) => {
          const body = (
            <>
              <span className={`h-7 w-7 shrink-0 rounded-full grid place-items-center text-xs font-black ${s.done ? "bg-primary text-primary-foreground" : s.id === nextId ? "border-2 border-primary text-primary" : "border text-muted-foreground"}`}>
                {s.done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block font-bold text-sm ${s.done ? "line-through text-muted-foreground" : ""}`}>{t(`guide.${s.id}`)}{s.optional && <span className="ms-1.5 text-[11px] font-semibold text-muted-foreground">({t("guide.optional")})</span>}</span>
                {!s.done && <span className="block text-xs text-muted-foreground">{t(`guide.${s.id}Sub`)}</span>}
              </span>
              {!s.done && (s.to || s.onClick) && <Arrow className="h-4 w-4 text-primary shrink-0" />}
            </>
          );
          const cls = `w-full flex items-center gap-3 rounded-2xl px-3 py-2.5 text-start transition-[background-color] ${s.id === nextId ? "bg-primary/5" : ""} ${!s.done ? "hover:bg-accent" : ""}`;
          const tid = `welcome-step-${s.id}`;
          return (
            <li key={s.id} data-testid={tid} data-done={s.done}>
              {s.done || (!s.to && !s.onClick) ? <div className={cls}>{body}</div>
                : s.to ? <Link to={s.to} className={cls} data-testid={`${tid}-action`}>{body}</Link>
                : <button type="button" onClick={s.onClick} className={cls} data-testid={`${tid}-action`}>{body}</button>}
            </li>
          );
        })}
      </ol>
    </section>
  );
};
